// src/components/ui.jsx
// Shared UI primitives: Chip, Spin, Btn, FI, Toggle, Modal, LinkBtn, NavItem, MobTab.

import { useState } from "react";
import { createPortal } from "react-dom";

export const Chip = ({label, color}) => (
  <span style={{display:"inline-flex",alignItems:"center",padding:"2px 10px",borderRadius:99,fontSize:11,fontWeight:600,background:color+"22",color,border:"1px solid "+color+"44",letterSpacing:.3}}>{label}</span>
);

export const Spin = () => (
  <span style={{display:"inline-block",animation:"spin .8s linear infinite"}}>⟳</span>
);

export const Btn = ({children, onClick, variant, disabled, style}) => {
  const vs = variant || "primary";
  const st = style || {};
  const v = {
    primary: {background:"var(--brown)", color:"#fff", border:"none"},
    ghost:   {background:"transparent",  color:"var(--brown)", border:"1.5px solid var(--warm3)"},
    soft:    {background:"var(--warm1)", color:"var(--brown)", border:"1px solid var(--warm2)"},
  };
  return (
    <button onClick={onClick} disabled={disabled}
      style={{padding:"9px 20px",borderRadius:99,fontSize:13,fontWeight:600,cursor:disabled?"not-allowed":"pointer",opacity:disabled?.5:1,fontFamily:"Barlow,sans-serif",...(v[vs]||v.primary),...st}}>
      {children}
    </button>
  );
};

export const FI = ({label, value, onChange, placeholder, type="text", multiline, rows=3}) => (
  <div style={{display:"flex",flexDirection:"column",gap:5}}>
    {label && <label style={{fontSize:12,fontWeight:600,color:"var(--muted)",textTransform:"uppercase",letterSpacing:.5}}>{label}</label>}
    {multiline
      ? <textarea value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} rows={rows}
          style={{padding:"10px 14px",borderRadius:10,border:"1.5px solid var(--warm2)",background:"var(--warm1)",fontSize:13,color:"var(--text)",resize:"vertical",outline:"none"}}
          onFocus={e=>e.target.style.borderColor="var(--warm3)"}
          onBlur={e=>e.target.style.borderColor="var(--warm2)"} />
      : <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
          style={{padding:"10px 14px",borderRadius:10,border:"1.5px solid var(--warm2)",background:"var(--warm1)",fontSize:13,color:"var(--text)",outline:"none"}}
          onFocus={e=>e.target.style.borderColor="var(--warm3)"}
          onBlur={e=>e.target.style.borderColor="var(--warm2)"} />
    }
  </div>
);

export const Toggle = ({checked, onChange, label}) => (
  <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
    <div onClick={()=>onChange(!checked)}
      style={{width:40,height:22,borderRadius:99,background:checked?"var(--brown)":"var(--warm2)",position:"relative",transition:"background .2s",flexShrink:0}}>
      <div style={{position:"absolute",top:3,left:checked?20:3,width:16,height:16,borderRadius:99,background:"#fff",transition:"left .2s",boxShadow:"0 1px 4px rgba(0,0,0,.18)"}} />
    </div>
    {label && <span style={{fontSize:13,color:"var(--text)"}}>{label}</span>}
  </label>
);

// Render via createPortal so the modal mounts directly at document.body.
// This sidesteps a CSS pitfall: any ancestor with a CSS `transform` (which
// our fadeUp animation uses) becomes the containing block for fixed-positioned
// descendants, breaking viewport-centered modals. With the portal, the modal
// is always positioned relative to the actual viewport regardless of where in
// the React tree it's declared.
export const Modal = ({title, children, onClose, wide}) => createPortal(
  <div onClick={e=>{ if(e.target===e.currentTarget) onClose(); }}
    style={{position:"fixed",inset:0,background:"rgba(45,32,23,.45)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:24}}>
    <div style={{background:"#fff",borderRadius:20,padding:32,width:"100%",maxWidth:wide?720:480,maxHeight:"90vh",overflowY:"auto",boxShadow:"var(--sh-lg)",animation:"fadeUp .3s ease both"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24}}>
        <h3 style={{fontSize:20}}>{title}</h3>
        <button onClick={onClose}
          style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:"var(--muted)",lineHeight:1,padding:"0 4px"}}>×</button>
      </div>
      {children}
    </div>
  </div>,
  document.body
);

export const LinkBtn = ({children, onClick}) => (
  <span onClick={e=>{e.stopPropagation();onClick();}}
    style={{color:"var(--brown)",fontWeight:600,cursor:"pointer",textDecoration:"underline",textDecorationStyle:"dotted",textUnderlineOffset:3}}>
    {children}
  </span>
);

export function NavItem({icon, label, tooltip, active, onClick, collapsed}) {
  const [hov, setHov] = useState(false);
  return (
    <div style={{position:"relative",marginBottom:4}}>
      <button onClick={onClick}
        onMouseEnter={()=>setHov(true)}
        onMouseLeave={()=>setHov(false)}
        style={{
          width:"100%",display:"flex",alignItems:"center",border:"none",cursor:"pointer",
          borderRadius:10,fontFamily:"Barlow,sans-serif",fontSize:14,
          transition:"background .15s,color .15s",
          background:active?"rgba(255,255,255,.15)":hov?"rgba(255,255,255,.08)":"transparent",
          color:active||hov?"#fff":"rgba(255,255,255,.55)",
          fontWeight:active?600:400,
          justifyContent:collapsed?"center":"flex-start",
          gap:collapsed?0:10,
          padding:collapsed?"11px 0":"10px 12px",
        }}>
        <span style={{fontSize:18,flexShrink:0,lineHeight:1}}>{icon}</span>
        <span style={{
          opacity:collapsed?0:1,
          maxWidth:collapsed?0:160,
          overflow:"hidden",
          transition:"opacity .2s,max-width .25s",
          display:"inline-block",
          whiteSpace:"nowrap",
        }}>{label}</span>
      </button>
      {collapsed && hov && (
        <div style={{
          position:"absolute",left:"calc(100% + 10px)",top:"50%",
          transform:"translateY(-50%)",
          background:"var(--dark)",color:"#fff",fontSize:12,fontWeight:500,
          padding:"5px 12px",borderRadius:8,whiteSpace:"nowrap",
          zIndex:999,pointerEvents:"none",fontFamily:"Barlow,sans-serif",
        }}>
          {tooltip || label}
          <div style={{
            position:"absolute",right:"100%",top:"50%",
            transform:"translateY(-50%)",
            border:"5px solid transparent",borderRightColor:"var(--dark)",
          }} />
        </div>
      )}
    </div>
  );
}

export function MobTab({icon, label, active, onClick}) {
  return (
    <button onClick={onClick}
      style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:2,padding:"6px 4px",background:"none",border:"none",cursor:"pointer",color:active?"var(--brown)":"var(--muted)"}}>
      <span style={{fontSize:20}}>{icon}</span>
      <span style={{fontSize:9,fontWeight:active?600:400,fontFamily:"Barlow,sans-serif"}}>
        {label.length>7?label.slice(0,7):label}
      </span>
    </button>
  );
}


// ── ActivitySummary ──────────────────────────────────────────────────────────
// Shows an AI-generated 2-sentence summary for an activity, with lazy loading.
//   - If a cached summary exists, renders it immediately
//   - If content too short, shows the raw content as-is (per Q3 -> don't summarize)
//   - If not yet generated, kicks off generation and shows a subtle '…' indicator
//   - If generation fails, shows '(no summary available)'
import { useEffect } from "react";
import * as Summaries from "../summaries.js";

export function ActivitySummary({activity, fallback}) {
  // activity is expected to have: {id, content, summary} -- the fallback prop
  // is what to show when content is too short (we pass the raw `summary` field
  // for that case so the UI still shows something).
  const id = activity?.id;
  const content = activity?.content || activity?.summary || "";

  // Local state to re-render when summary becomes available
  const [, force] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const unsub = Summaries.subscribe(() => { if (!cancelled) force(n => n + 1); });
    // Kick off generation if needed. getSummary handles the should-summarize
    // check and returns null for content that's too short.
    Summaries.getSummary(id, content);
    return () => { cancelled = true; unsub(); };
  }, [id, content]);

  // Synchronous check
  const cached = Summaries.getCachedSummary(id, content);

  // null means content too short -> show raw fallback
  if (cached === null) {
    return <span>{fallback || content}</span>;
  }
  // undefined means generation in progress
  if (cached === undefined) {
    return (
      <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
        <span style={{color:"var(--muted)",fontStyle:"italic",fontSize:12}}>Generating summary…</span>
      </span>
    );
  }
  // string -> the summary
  return <span style={{fontStyle:"italic"}}>{cached}</span>;
}

